import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import addNotification from '../utils/addNotification';
import { graphqlRequest, graphqlUploadRequest } from '../utils/graphqlClient';

type Label = { id: number; name: string };
type GpsPoint = { type?: string; coordinates?: number[] } | null;
type Picture = {
  id: number;
  name: string;
  place?: string | null;
  description?: string | null;
  customClass?: string | null;
  src?: string | null;
  thumb?: string | null;
  original?: string | null;
  isPublished?: boolean;
  isStarred?: boolean;
  labelId?: number | null;
  likes?: number;
  gps?: GpsPoint;
  [key: string]: any;
};

type AppContextState = {
  loading: boolean;
  labels: Label[];
  pictures: Picture[];
  labelsStats: Array<{ label: string; value: number }>;
  likesStats: Array<{ place: string; name: string; value: number }>;
  allGpsDatas: Picture[];
  currentGpsData: { gps?: GpsPoint; img?: string; title?: string };
  perPage: number;
  totalPages: number;
  isAdmin: boolean;
  username: string;
};

type AppContextValue = {
  state: AppContextState;
  actions: {
    setLoading: (value: boolean) => void;
    restoreAdmin: (username: string) => void;
    logout: () => void;
    login: (value: { username: string; password: string }) => Promise<void>;
    getLabels: () => Promise<void>;
    getPictures: () => Promise<void>;
    getLabelsStats: () => Promise<void>;
    getLikesStats: () => Promise<void>;
    getAllGPSDatas: () => Promise<void>;
    initData: () => Promise<void>;
    readUploadExif: (file: File) => Promise<{
      make?: string | null;
      model?: string | null;
      exposure?: number | null;
      fnumber?: number | null;
      iso?: string | null;
      focalDistance?: number | null;
      shootingDate?: string | null;
      lensInfo?: string | null;
      lensModel?: string | null;
    }>;
    addPicture: (value: any) => Promise<void>;
    modifyPicture: (value: any) => Promise<void>;
    deletePicture: (id: number | string) => Promise<void>;
    addLike: (id: number | string) => Promise<void>;
    toggleStarred: (id: number | string, starred: boolean) => Promise<void>;
    togglePublication: (id: number | string, published: boolean) => Promise<void>;
    getGpsData: (id: number | string) => void;
  };
};

const AppContext = createContext<AppContextValue | undefined>(undefined);

const PICTURE_FIELDS = `
  id
  name
  place
  description
  customClass
  src
  thumb
  original
  isPublished
  isStarred
  labelId
  likes
  gps {
    type
    coordinates
  }
  make
  model
  exposure
  fnumber
  iso
  focalDistance
  shootingDate
  lensInfo
  lensModel
`;

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState<Label[]>([]);
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [labelsStats, setLabelsStats] = useState<Array<{ label: string; value: number }>>([]);
  const [likesStats, setLikesStats] = useState<Array<{ place: string; name: string; value: number }>>([]);
  const [allGpsDatas, setAllGpsDatas] = useState<Picture[]>([]);
  const [currentGpsData, setCurrentGpsData] = useState<{ gps?: GpsPoint; img?: string; title?: string }>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState('');
  const perPage = 10;

  const getToken = () => localStorage.getItem('token');

  const getLabels = useCallback(async () => {
    const response = await graphqlRequest(`
      query GetLabels {
        labels {
          id
          name
        }
      }
    `);

    const data = response?.data?.data?.labels || [];
    setLabels(data.map((label: any) => ({ ...label, id: Number(label.id) })));
  }, []);

  const getPictures = useCallback(async () => {
    const token = getToken();
    const includeUnpublished = Boolean(token);
    const response = await graphqlRequest(`
      query GetPictures($includeUnpublished: Boolean) {
        pictures(includeUnpublished: $includeUnpublished) {
          ${PICTURE_FIELDS}
        }
      }
    `, { includeUnpublished }, token || undefined);

    const rawPictures = response?.data?.data?.pictures || [];
    const parsedPictures = rawPictures.map((picture: any) => ({
      ...picture,
      id: Number(picture.id),
      labelId: picture.labelId !== null ? Number(picture.labelId) : null,
      likes: typeof picture.likes === 'number' ? picture.likes : Number(picture.likes || 0),
      isPublished: typeof picture.isPublished === 'boolean' ? picture.isPublished : true,
    }));

    setPictures(parsedPictures);
  }, []);

  const getLabelsStats = useCallback(async () => {
    const response = await graphqlRequest(`
      query GetLabelsStats {
        labelsStats {
          label
          value
        }
      }
    `);

    setLabelsStats(response?.data?.data?.labelsStats || []);
  }, []);

  const getLikesStats = useCallback(async () => {
    const response = await graphqlRequest(`
      query GetLikesStats {
        likesStats {
          place
          name
          value
        }
      }
    `);

    setLikesStats(response?.data?.data?.likesStats || []);
  }, []);

  const getAllGPSDatas = useCallback(async () => {
    const response = await graphqlRequest(`
      query GetPicturesGps {
        picturesGps {
          id
          name
          place
          src
          thumb
          gps {
            type
            coordinates
          }
        }
      }
    `);

    const gpsPictures = (response?.data?.data?.picturesGps || []).map((picture: any) => ({
      ...picture,
      id: Number(picture.id),
    }));

    setAllGpsDatas(gpsPictures);
  }, []);

  const initData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([getLabels(), getPictures(), getLabelsStats(), getLikesStats(), getAllGPSDatas()]);
    } catch (error) {
      console.error('Failed to initialize app data', error?.response?.data || error.message || error);
      addNotification('init-error');
    } finally {
      setLoading(false);
    }
  }, [getAllGPSDatas, getLabels, getLabelsStats, getLikesStats, getPictures]);

  const restoreAdmin = useCallback((name: string) => {
    setIsAdmin(true);
    setUsername(name);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setIsAdmin(false);
    setUsername('');
  }, []);

  const login = useCallback(async (value: { username: string; password: string }) => {
    try {
      const response = await graphqlRequest(
        `
          mutation Login($username: String!, $password: String!) {
            login(username: $username, password: $password) {
              token
              username
            }
          }
        `,
        value
      );

      const payload = response?.data?.data?.login;
      if (!payload?.token || !payload?.username) {
        throw new Error('Invalid login payload');
      }

      localStorage.setItem('token', payload.token);
      localStorage.setItem('username', payload.username);
      setIsAdmin(true);
      setUsername(payload.username);
      await getPictures();
      addNotification('login-success');
    } catch (error) {
      addNotification('login-error');
      throw error;
    }
  }, [getPictures]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([getPictures(), getLabelsStats(), getAllGPSDatas(), getLikesStats()]);
  }, [getAllGPSDatas, getLabelsStats, getLikesStats, getPictures]);

  const addPicture = useCallback(
    async (value: any) => {
      setLoading(true);
      try {
        const token = getToken();
        if (!token) {
          throw new Error('Unauthorized');
        }

        await graphqlUploadRequest(
          `
            mutation UploadPicture($file: Upload!, $input: UploadPictureInput!) {
              uploadPicture(file: $file, input: $input) {
                id
              }
            }
          `,
          {
            file: null,
            input: {
              name: value.name,
              place: value.place,
              description: value.description,
              customClass: value.customClass,
              labelId: value.labelId ? Number(value.labelId) : null,
              lat: value.lat ? Number(value.lat) : null,
              lng: value.lng ? Number(value.lng) : null,
              isPublished: value.isPublished,
              make: value.make,
              model: value.model,
              exposure: value.exposure ? Number(value.exposure) : null,
              fnumber: value.fnumber ? Number(value.fnumber) : null,
              iso: value.iso,
              focalDistance: value.focalDistance ? Number(value.focalDistance) : null,
              shootingDate: value.shootingDate || null,
              lensInfo: value.lensInfo,
              lensModel: value.lensModel,
            },
          },
          value.file,
          token
        );

        addNotification('addEntry-success');
        await refreshAfterMutation();
      } catch (error) {
        addNotification('addEntry-error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [refreshAfterMutation]
  );

  const readUploadExif = useCallback(async (file: File) => {
    const token = getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }

    const response = await graphqlUploadRequest(
      `
        mutation ReadUploadExif($file: Upload!) {
          readUploadExif(file: $file) {
            make
            model
            exposure
            fnumber
            iso
            focalDistance
            shootingDate
            lensInfo
            lensModel
          }
        }
      `,
      {
        file: null,
      },
      file,
      token
    );

    return response?.data?.data?.readUploadExif || {};
  }, []);

  const modifyPicture = useCallback(
    async (value: any) => {
      setLoading(true);
      try {
        const token = getToken();
        if (!token) {
          throw new Error('Unauthorized');
        }

        await graphqlRequest(
          `
            mutation UpdatePicture($id: ID!, $input: PictureUpdateInput!) {
              updatePicture(id: $id, input: $input) {
                id
              }
            }
          `,
          {
            id: value.id,
            input: {
              name: value.name,
              place: value.place,
              description: value.description,
              customClass: value.customClass,
              isPublished: value.isPublished,
              labelId: value.labelId ? Number(value.labelId) : null,
              isStarred: value.isStarred,
              lat: value.lat ? Number(value.lat) : null,
              lng: value.lng ? Number(value.lng) : null,
              make: value.make,
              model: value.model,
              exposure: value.exposure ? Number(value.exposure) : null,
              fnumber: value.fnumber ? Number(value.fnumber) : null,
              iso: value.iso,
              focalDistance: value.focalDistance ? Number(value.focalDistance) : null,
            },
          },
          token
        );

        if (value.file) {
          await graphqlUploadRequest(
            `
              mutation ReplacePictureSource($id: ID!, $file: Upload!) {
                replacePictureSource(id: $id, file: $file) {
                  id
                }
              }
            `,
            {
              id: value.id,
              file: null,
            },
            value.file,
            token
          );
        }

        addNotification('modifyEntry-success');
        await refreshAfterMutation();
      } catch (error) {
        addNotification('modifyEntry-error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [refreshAfterMutation]
  );

  const deletePicture = useCallback(
    async (id: number | string) => {
      setLoading(true);
      try {
        const token = getToken();
        if (!token) {
          throw new Error('Unauthorized');
        }

        await graphqlRequest(
          `
            mutation DeletePicture($id: ID!) {
              deletePicture(id: $id)
            }
          `,
          { id },
          token
        );

        addNotification('deleteEntry-success');
        await refreshAfterMutation();
      } catch (error) {
        addNotification('deleteEntry-error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [refreshAfterMutation]
  );

  const addLike = useCallback(
    async (id: number | string) => {
      await graphqlRequest(
        `
          mutation AddLike($id: ID!) {
            addLike(id: $id) {
              id
            }
          }
        `,
        { id }
      );

      await Promise.all([getPictures(), getLabelsStats(), getLikesStats()]);
    },
    [getLabelsStats, getLikesStats, getPictures]
  );

  const toggleStarred = useCallback(
    async (id: number | string, starred: boolean) => {
      try {
        const token = getToken();
        if (!token) {
          throw new Error('Unauthorized');
        }

        await graphqlRequest(
          `
            mutation ToggleStarred($id: ID!, $starred: Boolean!) {
              toggleStarred(id: $id, starred: $starred) {
                id
              }
            }
          `,
          { id, starred: !starred },
          token
        );

        await getPictures();
      } catch (error) {
        console.error(error);
      }
    },
    [getPictures]
  );

  const togglePublication = useCallback(
    async (id: number | string, published: boolean) => {
      try {
        const token = getToken();
        if (!token) {
          throw new Error('Unauthorized');
        }

        await graphqlRequest(
          `
            mutation TogglePublication($id: ID!, $published: Boolean!) {
              togglePublication(id: $id, published: $published) {
                id
                isPublished
              }
            }
          `,
          { id, published: !published },
          token
        );

        await getPictures();
      } catch (error) {
        addNotification('modifyEntry-error');
        throw error;
      }
    },
    [getPictures]
  );

  const getGpsData = useCallback(
    (id: number | string) => {
      const targetPicture = pictures.find((picture) => picture.id === Number(id));
      if (!targetPicture) {
        setCurrentGpsData({});
        return;
      }

      setCurrentGpsData({
        gps: targetPicture.gps,
        img: targetPicture.src || '',
        title: targetPicture.place || '',
      });
    },
    [pictures]
  );

  const state = useMemo(
    () => ({
      loading,
      labels,
      pictures,
      labelsStats,
      likesStats,
      allGpsDatas,
      currentGpsData,
      perPage,
      totalPages: Math.ceil((pictures?.length || 0) / perPage),
      isAdmin,
      username,
    }),
    [
      allGpsDatas,
      currentGpsData,
      isAdmin,
      labels,
      labelsStats,
      likesStats,
      loading,
      pictures,
      username,
    ]
  );

  const actions = useMemo(
    () => ({
      setLoading,
      restoreAdmin,
      logout,
      login,
      getLabels,
      getPictures,
      getLabelsStats,
      getLikesStats,
      getAllGPSDatas,
      initData,
      readUploadExif,
      addPicture,
      modifyPicture,
      deletePicture,
      addLike,
      toggleStarred,
      togglePublication,
      getGpsData,
    }),
    [
      addLike,
      addPicture,
      deletePicture,
      getAllGPSDatas,
      getGpsData,
      getLabels,
      getLabelsStats,
      getLikesStats,
      getPictures,
      initData,
      readUploadExif,
      login,
      logout,
      modifyPicture,
      restoreAdmin,
      toggleStarred,
      togglePublication,
    ]
  );

  return <AppContext.Provider value={{ state, actions }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};
